import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, RotateCw } from 'lucide-react';
import { z } from 'zod';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router';
import { tenantsApi } from '@/lib/api/tenants';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import type { TenantCreateInput, TenantUpdateInput } from '@/types/entities';
import { PageHeader, PageSection } from '@/components/layouts/page-layout';

type Props = {
  type: 'create' | 'edit';
};

const SLUG_REGEX = /^[a-z0-9-]+$/;
const URL_REGEX = /^https?:\/\/.+/i;

export function AdminTenantsForm({ type }: Props) {
  const { id } = useParams();
  const navigate = useNavigate();

  const formSchema = z.object({
    name: z.string().min(1, 'Name is required'),
    slug: z.string()
      .min(1, 'Slug is required')
      .transform((val) => val.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-'))
      .pipe(z.string().regex(SLUG_REGEX, 'Slug must contain only lowercase letters, numbers, and hyphens')),
    website_url: z.string().min(1, 'Website URL is required').regex(URL_REGEX, 'Website URL must be a valid URL (e.g., https://example.com)')
  });

  type FormValues = z.infer<typeof formSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      slug: '',
      website_url: '',
    },
    mode: 'onTouched',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    async function fetchTenant() {
      if (type === 'edit' && id) {
        try {
          const tenant = await tenantsApi.getById(Number(id));
          form.reset({
            name: tenant.name || "",
            slug: tenant.slug || "",
            website_url: tenant.website_url || "",
          });
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to load tenant');
        }
      } else {
        form.reset({
          name: "",
          slug: "",
          website_url: "",
        });
      }
      setIsLoading(false);
    }
    fetchTenant();
  }, [type, id, form]);

  const handleSubmit = async (values: FormValues) => {
    setError(null);

    setIsSubmitting(true);
    try {
      if (type === 'edit' && id) {
        const data: TenantUpdateInput = {
          name: values.name.trim(),
          slug: values.slug, // Already normalized by zod transform
          website_url: values.website_url.trim(),
        };
        await tenantsApi.update(Number(id), data);
        toast.success('Tenant updated successfully');
      } else {
        const data: TenantCreateInput = {
          name: values.name.trim(),
          slug: values.slug, // Already normalized by zod transform
          website_url: values.website_url.trim(),
        };
        await tenantsApi.create(data);
        toast.success('Tenant created successfully');
      }
      navigate('/admin/dashboard/tenants');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      setError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const generateSlugFromName = () => {
    const currentName = form.getValues('name');
    const generatedSlug = currentName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');

    form.setValue('slug', generatedSlug, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6">
      <PageHeader
        title={type === 'edit' ? 'Edit Tenant' : 'Create New Tenant'}
        description={
          type === 'edit'
            ? 'Update this tenant workspace configuration.'
            : 'Configure a new workspace instance. You can update settings later from tenant controls.'
        }
      />

      <PageSection className="rounded-2xl border border-border bg-card p-6 shadow-sm md:p-8">
        {isLoading ? (
          <div className="flex min-h-[220px] items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 size-5 animate-spin" />
            Loading tenant data...
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
              <div className="grid gap-8 md:grid-cols-2">
                <div className="space-y-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">
                    Primary Identity
                  </p>

                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tenant Name</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. Acme Corporation" autoComplete="off" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="website_url"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Website URL</FormLabel>
                        <FormControl>
                          <Input placeholder="https://www.example.com" autoComplete="off" {...field} />
                        </FormControl>
                        <FormDescription>
                          Validation follows standard HTTPS/HTTP URL format.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="space-y-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">
                    System Routing
                  </p>

                  <FormField
                    control={form.control}
                    name="slug"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>System Slug</FormLabel>
                        <div className="flex items-center gap-2">
                          <FormControl>
                            <Input placeholder="acme-corp" autoComplete="off" {...field} />
                          </FormControl>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={generateSlugFromName}
                            title="Generate slug from tenant name"
                            aria-label="Generate slug from tenant name"
                          >
                            <RotateCw className="size-4" />
                          </Button>
                        </div>
                        <FormDescription>
                          Unique identifier used for routing. Lowercase letters, numbers, and hyphens only.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="rounded-lg border border-border bg-muted/50 p-3 text-sm text-muted-foreground">
                    Tenant slug is used in API and dashboard routes. Keep it stable after launch.
                  </div>
                </div>
              </div>

              <div className="border-t border-border pt-6">
                {error && (
                  <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                    {error}
                  </div>
                )}

                <div className="flex items-center justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isSubmitting}
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Saving...' : type === 'edit' ? 'Save Changes' : 'Create Tenant'}
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        )}
      </PageSection>

      {!isLoading && (
        <div className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
          New tenants will appear in the dashboard list immediately after creation.
        </div>
      )}
    </div>
  );
}
